import React, { useState, useContext, useEffect, useImperativeHandle } from "react";

// @mui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import AddBoxIcon from "@mui/icons-material/AddBox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Settings from "examples/Icons/Settings";
import Tooltip from "@mui/material/Tooltip";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiBadge from "components/SuiBadge";
import Icon from "@mui/material/Icon";

import { ErrorMessage } from "@hookform/error-message";

import toast from "react-hot-toast";

import { capitalCase } from "capital-case";
import { MANUFACTURER_SERVICES } from "utils/data";

const TAG_MAX_LENGTH = 25;
const TAG_MAX_QUANTITY = 12;
const SERVICE_DESC_MAX_LENGTH = 500;

const ServicesRequired = React.forwardRef(({ register }, ref) => {
  const [checked, setChecked] = useState({});
  const [selectedTags, setSelectedTags] = useState({});
  const [serviceDesc, setServiceDesc] = useState();

  useImperativeHandle(
    ref,
    () => ({
      checked,
      selectedTags,
      serviceDesc,
      setChecked,
      setSelectedTags,
      setServiceDesc,
    }),
    [checked, selectedTags]
  );

  const handleSelectTag = (value, step) => {
    if (!selectedTags[step]) {
      selectedTags[step] = [];
    }
    if (selectedTags[step] && selectedTags[step].includes(value)) {
      let newSelectedTags = { ...selectedTags };
      newSelectedTags[step] = selectedTags[step].filter((x) => x != value);
      setSelectedTags(newSelectedTags);
    } else {
      if (selectedTags[step] && selectedTags[step].length >= TAG_MAX_QUANTITY) {
        return toast.error(`You can have at most ${TAG_MAX_QUANTITY} tags!`);
      }
      let newSelectedTags = { ...selectedTags };
      newSelectedTags[step] = [...newSelectedTags[step], value];

      setSelectedTags(newSelectedTags);
    }
  };

  const customButtonStyles = ({
    functions: { pxToRem, rgba },
    borders: { borderWidth },
    palette: { transparent, dark, info, white, secondary },
  }) => ({
    width: pxToRem(100),
    height: pxToRem(80),
    borderWidth: borderWidth[2],
    mb: 0.5,
    ml: 0.5,
    size: "large",

    "&.MuiButton-contained, &.MuiButton-contained:hover": {
      boxShadow: "none",
      border: `${borderWidth[2]} solid ${transparent.main}`,
    },

    "&:hover": {
      backgroundColor: `${dark.main} !important`,
      border: `${borderWidth[2]} solid ${secondary.main} !important`,
      color: white.main,
    },
  });

  return (
    <SuiBox>
      <Grid container spacing={1}>
        {MANUFACTURER_SERVICES.map((item, index) => {
          return (
            <Grid item xs={12} sm={12} md={12} lg={index == 1 || index == 3 ? 7 : 5}>
              <SuiBox sx={{ px: 2, py: 1 }}>
                <SuiBox display="flex" justifyContent="start" gap={1} py={1}>
                  <SuiBox textAlign="center">
                    <Tooltip title={item.hint}>
                      <SuiButton
                        iconOnly={true}
                        size="large"
                        color="secondary"
                        variant={checked[item.value] ? "contained" : "outlined"}
                        onClick={() => {
                          //check or uncheck parent if click
                          let newChecked = { ...checked };
                          newChecked[item.value] = !newChecked[item.value];
                          setChecked(newChecked);
                          if (newChecked[item.value] == false) {
                            //if uncheck parent, then uncheck all children
                            let newSelectedTags = { ...selectedTags };
                            newSelectedTags[item.value] = [];
                            setSelectedTags(newSelectedTags);
                          }
                        }}
                        sx={customButtonStyles}
                      >
                        <Icon color={checked[item.value] ? "white" : "dark"} fontSize="large">
                          {item.icon}
                        </Icon>
                        {/* <Settings size="24px" color={checked[item.value] ? "white" : "dark"} />*/}
                      </SuiButton>
                    </Tooltip>
                    <SuiTypography variant="body1" fontWeight="bold">
                      {item.label}
                    </SuiTypography>
                  </SuiBox>

                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <div className={`flex flex-col flex-wrap gap-x-6`}>
                      {item.children.map((cat, index) => (
                        <div className="ml-1">
                          <Checkbox
                            key={index}
                            variant="contained"
                            onClick={() => {
                              if (checked[item.value]) {
                                //if parent already checked, then check the children
                                handleSelectTag(cat.value, item.value);
                              } else {
                                //if parent is uncheckd, then check the parent if children is checked
                                if (
                                  (selectedTags[item.value] &&
                                    !selectedTags[item.value].includes(cat.value)) ||
                                  !selectedTags[item.value]
                                ) {
                                  let newChecked = { ...checked };
                                  newChecked[item.value] = true;
                                  setChecked(newChecked);
                                }

                                handleSelectTag(cat.value, item.value);
                              }
                            }}
                            checked={
                              selectedTags[item.value] &&
                              selectedTags[item.value].includes(cat.value)
                                ? true
                                : false
                            }
                            className="mr-1 my-1 hover:cursor-pointer"
                          />
                          <SuiTypography
                            component="label"
                            variant="body1"
                            fontWeight="regular"
                            textTransform="capitalize"
                            color={checked[item.value] ? "dark" : "secondary"}
                            onClick={() => {
                              if (checked[item.value]) {
                                handleSelectTag(cat.value, item.value);
                              }
                            }}
                            className="hover:cursor-pointer"
                          >
                            {capitalCase(cat.label, { stripRegexp: /[^A-Z0-9\'\&\/\-\_]/gi })}
                          </SuiTypography>
                        </div>
                      ))}
                    </div>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </Grid>
          );
        })}
      </Grid>
      <SuiBox display="flex" flexDirection="column" justifyContent="flex-end" height="100%">
        <SuiBox mb={1} ml={0.5} mt={2} lineHeight={0} display="inline-block">
          <SuiTypography component="label" variant="caption" fontWeight="bold">
            Special Instruction
          </SuiTypography>
        </SuiBox>

        <SuiInput
          multiline
          rows="4"
          componentsProps={{ input: { maxlength: SERVICE_DESC_MAX_LENGTH } }}
          {...register("servicesDescription", {
            value: serviceDesc,
            onChange: (e) => {
              setServiceDesc(e.target.value);
            },
          })}
          placeholder="You can provide details for services you need at here."
        />

        <SuiBox display="flex" justifyContent="space-between">
          <div></div>
          <SuiTypography variant="text" color="secondary" fontWeight="regular" fontSize="14px">
            {serviceDesc ? serviceDesc.length : 0}/{SERVICE_DESC_MAX_LENGTH}
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    </SuiBox>
  );
});

export default ServicesRequired;
